import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const QUANTITY_UPDATE_EVENT = 'quantity-selector:update';

/**
 * @param {unknown} value
 * @returns {number}
 */
function normalizeQuantity(value) {
  const quantity = Number.parseInt(String(value), 10);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

/**
 * @param {string} template
 * @param {string} amount
 * @returns {string}
 */
function savingsLabel(template, amount) {
  return (template || 'Save __amount__').split('__amount__').join(amount);
}

/**
 * @param {string} template
 * @param {number} quantity
 * @returns {string}
 */
function itemCountLabel(template, quantity) {
  return (template || '__count__').split('__count__').join(String(quantity));
}

class GioQuantityPricePreview extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Element | Document | null} */
  #section = null;

  /** @type {WeakMap<HTMLElement, { html: string, ariaLive: string | null, ariaAtomic: string | null, dynamic: boolean }>} */
  #baseStates = new WeakMap();

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#section = this.closest('[id^="shopify-section-"]') || this.closest('section') || document;
    const { signal } = this.#controller;

    this.addEventListener('change', this.#handleOptionChange, { signal });
    this.#section.addEventListener(QUANTITY_UPDATE_EVENT, this.#handleQuantityChange, { signal });
    this.#section.addEventListener('input', this.#handleQuantityChange, { signal });
    this.#section.addEventListener('change', this.#handleQuantityChange, { signal });
    this.#section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.#render(this.#quantityInput()?.value || 1);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @returns {HTMLInputElement | null} */
  #quantityInput() {
    const form = this.#section?.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    const input = form?.querySelector('input[name="quantity"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {HTMLElement | null} */
  #priceContainer() {
    const selector = `product-price[data-product-id="${this.dataset.productId || ''}"] [ref="priceContainer"][data-gio-base-unit-cents]`;
    const container = this.#section?.querySelector(selector) || document.querySelector(selector);
    return container instanceof HTMLElement ? container : null;
  }

  /** @returns {HTMLElement | null} */
  #walletAmount() {
    const amount = this.#section?.querySelector('[data-gio-product-wallet-amount]');
    return amount instanceof HTMLElement ? amount : null;
  }

  /**
   * @param {number} discountedTotalCents
   * @param {string} moneyFormat
   * @param {string} currency
   */
  #renderWallet(discountedTotalCents, moneyFormat, currency) {
    const amount = this.#walletAmount();
    const walletRate = Number(this.dataset.walletRate || 0);
    if (!amount || !Number.isFinite(walletRate) || walletRate <= 0) return;

    const walletCents = Math.round((discountedTotalCents * walletRate) / 100);
    amount.textContent = formatMoney(walletCents, moneyFormat, currency);
  }

  /** @param {HTMLElement} container */
  #baseState(container) {
    const existing = this.#baseStates.get(container);
    if (existing) return existing;

    const state = {
      html: container.innerHTML,
      ariaLive: container.getAttribute('aria-live'),
      ariaAtomic: container.getAttribute('aria-atomic'),
      dynamic: false,
    };
    this.#baseStates.set(container, state);
    return state;
  }

  /** @param {HTMLElement} container */
  #restore(container) {
    const state = this.#baseState(container);
    if (!state.dynamic) return;

    container.innerHTML = state.html;
    if (state.ariaLive === null) container.removeAttribute('aria-live');
    else container.setAttribute('aria-live', state.ariaLive);
    if (state.ariaAtomic === null) container.removeAttribute('aria-atomic');
    else container.setAttribute('aria-atomic', state.ariaAtomic);
    state.dynamic = false;
  }

  /** @param {unknown} value */
  #render(value) {
    const quantity = normalizeQuantity(value);
    const container = this.#priceContainer();
    if (!container) return;

    const baseUnitCents = Number(container.dataset.gioBaseUnitCents || 0);
    if (!Number.isFinite(baseUnitCents) || baseUnitCents <= 0) return;

    const currency = this.dataset.currency || 'USD';
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    if (quantity === 1) {
      this.#restore(container);
      this.#renderWallet(baseUnitCents, moneyFormat, currency);
      return;
    }

    const percent = quantity >= 3 ? 15 : 10;
    const originalTotal = baseUnitCents * quantity;
    const saving = Math.round((originalTotal * percent) / 100);
    const discountedTotal = Math.max(0, originalTotal - saving);
    const discountedUnit = Math.round(discountedTotal / quantity);
    const approximateUnit = discountedTotal % quantity !== 0;
    const unitText = `${approximateUnit ? '≈' : ''}${formatMoney(discountedUnit, moneyFormat, currency)}`;
    const totalText = formatMoney(discountedTotal, moneyFormat, currency);
    const savingText = formatMoney(saving, moneyFormat, currency);
    const state = this.#baseState(container);

    const unitLine = document.createElement('span');
    unitLine.className = 'gio-quantity-price__unit-line';

    const unit = document.createElement('span');
    unit.className = 'price gio-quantity-price__unit';
    unit.dataset.gioQuantityUnit = '';
    unit.textContent = unitText;

    const perItem = document.createElement('span');
    perItem.className = 'gio-quantity-price__per-item';
    perItem.dataset.gioQuantityPerItem = '';
    perItem.textContent = this.dataset.perItemLabel || '';

    unitLine.append(unit, perItem);

    const summary = document.createElement('span');
    summary.className = 'gio-quantity-price__summary';

    const itemCount = document.createElement('span');
    itemCount.dataset.gioQuantityCount = '';
    itemCount.textContent = itemCountLabel(this.dataset.itemTemplate || '', quantity);

    const totalLabel = document.createElement('span');
    totalLabel.dataset.gioQuantityTotalLabel = '';
    totalLabel.textContent = this.dataset.totalLabel || '';

    const total = document.createElement('span');
    total.className = 'gio-quantity-price__total';
    total.dataset.gioQuantityTotal = '';
    total.textContent = totalText;

    const savingAmount = document.createElement('span');
    savingAmount.className = 'gio-quantity-price__saving';
    savingAmount.dataset.gioQuantitySaving = '';
    savingAmount.textContent = savingsLabel(this.dataset.saveTemplate || '', savingText);

    summary.append(itemCount, ' · ', totalLabel, ' ', total, ' · ', savingAmount);
    container.replaceChildren(unitLine, summary);
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'true');
    state.dynamic = true;
    this.#renderWallet(discountedTotal, moneyFormat, currency);
  }

  /** @param {Event} event */
  #handleOptionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-gio-qty-option]')) return;
    this.#render(event.target.value);
  };

  /** @param {Event & { detail?: { quantity?: number } }} event */
  #handleQuantityChange = (event) => {
    const input = this.#quantityInput();
    if (!input) return;

    if (event.type === QUANTITY_UPDATE_EVENT) {
      this.#render(event.detail?.quantity || input.value);
      return;
    }
    if (event.target !== input) return;
    this.#render(input.value);
  };

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #handleProductSelect = (event) => {
    const refresh = () => requestAnimationFrame(() => this.#render(this.#quantityInput()?.value || 1));
    if (!event.promise) {
      refresh();
      return;
    }
    Promise.resolve(event.promise).then(refresh).catch(() => null);
  };
}

if (!customElements.get('gio-quantity-price-preview')) {
  customElements.define('gio-quantity-price-preview', GioQuantityPricePreview);
}
